import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  ArrowForward,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { voucherService } from '../services/api';
import { Voucher, StatusHistoryResponse } from '../types/voucher';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`voucher-tabpanel-${index}`}
      aria-labelledby={`voucher-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface CopyButtonProps {
  text: string;
  label?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label || 'to clipboard'}`}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          ml: 1,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {copied ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const VoucherDetail: React.FC = () => {
  const { voucherNumber } = useParams<{ voucherNumber: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryResponse[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const { data: voucher, isLoading, error } = useQuery<Voucher>(
    ['voucher', voucherNumber],
    () => voucherService.getVoucher(voucherNumber!),
    {
      enabled: !!voucherNumber,
      onSuccess: (data) => {
        // Fetch status history
        voucherService.getVoucherStatusHistory(data.voucherNumber)
          .then(history => {
            console.log('Status history received:', history);
            setStatusHistory(history);
          })
          .catch(error => {
            console.error('Error fetching status history:', error);
          });
      },
    }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Fetch transactions when Transactions tab is selected
    if (newValue === 2 && transactions.length === 0 && voucherNumber) {
      fetchTransactions();
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const data = await voucherService.getVoucherTransactions(voucherNumber!);
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AWAITING_PAYMENT':
        return 'warning';
      case 'CUSTOMER_PAID':
        return 'success';
      case 'AWAITING_RETAILER_SETTLEMENT':
        return 'info';
      case 'AWAITING_MERCHANT_SETTLEMENT':
        return 'info';
      case 'SETTLED':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'REFUNDED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !voucher) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/voucher/management')}
          sx={{ mb: 2 }}
        >
          Back to Vouchers
        </Button>
        <Alert severity="error">
          Voucher not found. Please check the voucher number and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/voucher/management')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Voucher Details
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Chip
            label={getStatusLabel(voucher.voucherStatus)}
            color={getStatusColor(voucher.voucherStatus) as any}
            sx={{ fontSize: '1rem', padding: '8px 16px', height: 'auto' }}
          />
          {voucher.voucherStatusDescription && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {voucher.voucherStatusDescription}
            </Typography>
          )}
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="voucher detail tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<ReceiptIcon />} label="Details" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label="Status History" iconPosition="start" />
          <Tab icon={<PaymentIcon />} label="Transactions" iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Voucher Number
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                      {voucher.voucherNumber}
                    </Typography>
                    <CopyButton text={voucher.voucherNumber} label="voucher number" />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {voucher.currency} {voucher.amount.toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Merchant ID
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" gutterBottom sx={{ mb: 0 }}>
                      {voucher.merchantId}
                    </Typography>
                    <CopyButton text={voucher.merchantId} label="merchant ID" />
                  </Box>
                </Grid>

                {voucher.merchantName && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Merchant Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.merchantName}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Order Reference
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" gutterBottom sx={{ mb: 0 }}>
                      {voucher.merchantOrderReference}
                    </Typography>
                    <CopyButton text={voucher.merchantOrderReference} label="order reference" />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Product Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {voucher.productDescription}
                  </Typography>
                </Grid>

                {voucher.dateCreated && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(voucher.dateCreated), 'PPpp')}
                    </Typography>
                  </Grid>
                )}

                {voucher.expiresAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Expires At
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(voucher.expiresAt), 'PPpp')}
                    </Typography>
                  </Grid>
                )}

                {voucher.paidAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Paid At
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(voucher.paidAt), 'PPpp')}
                    </Typography>
                  </Grid>
                )}

                {voucher.retailerId && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Retailer ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.retailerId}
                    </Typography>
                  </Grid>
                )}

                {voucher.retailerPaymentRef && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Retailer Payment Reference
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.retailerPaymentRef}
                    </Typography>
                  </Grid>
                )}

                {voucher.settledWithRetailerAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Settled with Retailer At
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.settledWithRetailerAt ? format(new Date(voucher.settledWithRetailerAt), 'PPpp') : 'N/A'}
                    </Typography>
                  </Grid>
                )}

                {voucher.settledWithMerchantAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Settled with Merchant At
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.settledWithMerchantAt ? format(new Date(voucher.settledWithMerchantAt), 'PPpp') : 'N/A'}
                    </Typography>
                  </Grid>
                )}

                {voucher.retailerCommissionAmount !== undefined && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Retailer Commission
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.currency} {voucher.retailerCommissionAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                )}

                {voucher.merchantCommissionAmount !== undefined && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Merchant Commission
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {voucher.currency} {voucher.merchantCommissionAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Technical Details
                  </Typography>
                </Grid>

                {voucher.id && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      ID
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" gutterBottom sx={{ mb: 0, fontFamily: 'monospace' }}>
                        {voucher.id}
                      </Typography>
                      <CopyButton text={voucher.id} label="ID" />
                    </Box>
                  </Grid>
                )}

                {voucher.version !== undefined && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Version
                    </Typography>
                    <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace' }}>
                      {voucher.version}
                    </Typography>
                  </Grid>
                )}

                {voucher.lastUpdated && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(voucher.lastUpdated), 'PPpp')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {statusHistory.length === 0 ? (
            <Alert severity="info">
              No status history available for this voucher.
            </Alert>
          ) : (
            <Card>
              <CardContent>
                <List>
                  {statusHistory.map((history, index) => (
                    <React.Fragment key={history.id}>
                      <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', py: 2 }}>
                        <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                          <Typography variant="caption" color="textSecondary">
                            {format(new Date(history.transitionDate), 'PPpp')}
                            {history.timeSinceTransition && ` (${history.timeSinceTransition})`}
                          </Typography>
                          {history.isAutomatic && (
                            <Chip label="Automatic" size="small" variant="outlined" />
                          )}
                        </Box>

                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {history.fromStatus && (
                            <>
                              <Chip
                                label={getStatusLabel(history.fromStatus)}
                                size="small"
                                color={getStatusColor(history.fromStatus) as any}
                              />
                              <ArrowForward fontSize="small" />
                            </>
                          )}
                          <Chip
                            label={getStatusLabel(history.toStatus)}
                            size="small"
                            color={getStatusColor(history.toStatus) as any}
                          />
                        </Box>

                        {history.changeReason && (
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            <strong>Reason:</strong> {history.changeReason}
                          </Typography>
                        )}

                        {history.comments && (
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            <strong>Comments:</strong> {history.comments}
                          </Typography>
                        )}

                        {history.changedBy && (
                          <Typography variant="caption" color="textSecondary">
                            Changed by: {history.changedBy}
                          </Typography>
                        )}

                        {history.triggerEvent && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            Trigger: {history.triggerEvent}
                          </Typography>
                        )}
                      </ListItem>
                      {index < statusHistory.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {loadingTransactions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : transactions.length === 0 ? (
            <Alert severity="info">
              No transactions found for this voucher.
            </Alert>
          ) : (
            <Card>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Transaction Reference</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id} hover>
                          <TableCell>
                            {transaction.transactionDate
                              ? format(new Date(transaction.transactionDate), 'PPp')
                              : '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {transaction.transactionRef || transaction.transactionReference || '-'}
                          </TableCell>
                          <TableCell>{transaction.description || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.transactionType || transaction.type || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {transaction.currency} {transaction.totalAmount?.toFixed(2) || transaction.amount?.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status || transaction.transactionStatus || 'PENDING'}
                              color={
                                transaction.status === 'COMPLETED' || transaction.transactionStatus === 'COMPLETED'
                                  ? 'success'
                                  : transaction.status === 'FAILED' || transaction.transactionStatus === 'FAILED'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default VoucherDetail;
